import useIsMobile from '@/hooks/use-is-mobile';
import { useLoadingRouter } from '@/hooks/use-loading-router';
import debounce from 'lodash.debounce';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import HoverPrefetchLink from '../hover-prefetch-link';

const CategoryNoResult = props => {
  const { url_key } = props;
  const router = useLoadingRouter();

  const { register, handleSubmit } = useForm({
    defaultValues: {
      query: '',
    },
  });

  return (
    <div className=' py-10 lg_pb-[213px] lg_pt-[110px]'>
      <div className='px-0.5 lg_px-0'>
        <h2 className='text-center font-Shango text-[22px] leading-none lg_text-[34px] lg_leading-[1.2]'>
          SORRY, WE COULD&apos;T FIND ANY PRODUCTS.
        </h2>
        <p className='mt-5 text-center text-[18px] leading-[1.4]'>
          Please&nbsp;
          <Link
            className='underline'
            href={`/${url_key}`}
          >
            remove all filters
          </Link>
          &nbsp;to expand your results or try searching interested in.
        </p>
      </div>
      <div className='mt-10 px-1.5 lg_mt-[60px] lg_px-0'>
        <form
          className=' flex flex-col items-center justify-center gap-y-[15px] lg_flex-row lg_gap-x-[5px]'
          onSubmit={handleSubmit(values => {
            router.push(`/search.html?query=${values.query}`);
          })}
        >
          <input
            placeholder='Want to try searching interested in?'
            className='h-[50px] w-full border-1 border-solid border-E7DED5 px-[15px] text-[14px] placeholder_text-aba6a0 lg_max-w-[384px] lg_text-[15px]'
            suppressHydrationWarning
            {...register('query', {
              validate: value => {
                if (!value || !value.trim()) {
                  return 'This field is required.';
                }
              },
            })}
          />
          <button
            type='submit'
            className='flex h-12 w-full items-center justify-center rounded-[3px] border-1 border-solid border-80583F bg-80583F text-center text-white outline-0 lg_w-auto lg_px-[30px] lg_hover_bg-white lg_hover_text-80583F'
          >
            SEARCH
          </button>
        </form>
      </div>

      <CategoryRecommend />
    </div>
  );
};

const CategoryRecommend = () => {
  const scrollElementRef = useRef<HTMLDivElement>(null);
  const mockScrollBarRef = useRef<HTMLDivElement>(null);
  const [mockScrollBarWidth, setMockScrollBarWidth] = useState(0);
  const [showMockScrollBar, setShowMockScrollBar] = useState(true);
  const [mockScrollBarTranslateDistance, setMockScrollBarTranslateDistance] = useState(0);
  const isMobile = useIsMobile();

  const handleScroll = e => {
    const targetElement = e.target;

    const scrollWidth = targetElement.scrollWidth;
    const offsetWidth = targetElement.offsetWidth;

    if (offsetWidth === 0 || scrollWidth === 0 || scrollWidth < offsetWidth) {
      return;
    }

    setMockScrollBarTranslateDistance(
      (targetElement.scrollLeft / (scrollWidth - offsetWidth)) * (mockScrollBarWidth * 0.53),
    );
  };

  useEffect(() => {
    if (!isMobile) return;

    const initScrollInfo = () => {
      const scrollElement = scrollElementRef.current;
      const mockScrollBar = mockScrollBarRef.current;

      if (scrollElement) {
        setShowMockScrollBar(scrollElement.scrollWidth > scrollElement.clientWidth);
      }

      if (mockScrollBar) {
        setMockScrollBarWidth(mockScrollBar.offsetWidth);
      }
    };

    initScrollInfo();

    const resizeHandler = debounce(initScrollInfo, 500);

    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [isMobile]);

  return (
    <>
      <div className='overflow-hidden px-0.5 lg_px-0'>
        <div
          className='-mb-5 overflow-auto pb-5 lg_mb-0 lg_pb-0'
          onScroll={handleScroll}
          ref={scrollElementRef}
        >
          <ul className='mt-10 flex items-start gap-x-[15px] lg_mt-[110px] lg_flex-wrap lg_justify-center lg_gap-[27px]'>
            {[
              {
                imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/category/no-result/bridesmaid-dresses.webp',
                label: 'BRIDESMAID DRESSES',
                href: '/bridesmaid-dresses',
              },
              {
                imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/category/no-result/wedding-dresses.webp',
                label: 'WEDDING DRESSES',
                href: '/wedding-dresses',
              },
              {
                imageUrl:
                  process.env.NEXT_PUBLIC_BASE_PATH + '/images/category/no-result/mother-of-the-bride-dresses.webp',
                label: 'MOTHER OF THE BRIDE DRESSES',
                href: '/mother-of-the-bride-dresses',
              },
              {
                imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/category/no-result/wedding-guest-dresses.webp',
                label: 'WEDDING GUEST DRESSES',
                href: '/wedding-guest-dresses',
              },
              {
                imageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/category/no-result/prom-dresses.webp',
                label: 'PROM DRESSES',
                href: '/prom-dresses',
              },
            ].map(({ imageUrl, label, href }) => {
              return (
                <li
                  className='flex-none basis-[150px] lg_basis-[220px]'
                  key={href}
                >
                  <HoverPrefetchLink
                    className='block text-[0] '
                    href={href}
                  >
                    <Image
                      src={imageUrl}
                      alt={label}
                      width={220}
                      height={320}
                      unoptimized
                    />
                  </HoverPrefetchLink>
                  <HoverPrefetchLink
                    href={href}
                    className='mx-auto mt-[5px] block w-max max-w-[150px] text-center text-[15px] leading-[1.4] lg_mt-2.5 lg_max-w-[220px]'
                  >
                    {label}
                  </HoverPrefetchLink>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      {showMockScrollBar && isMobile ? (
        <div
          className='mt-[15px] h-1.5 px-0.5 lg_hidden'
          ref={mockScrollBarRef}
        >
          <div
            className='h-full w-[47%] bg-E7DED5'
            style={{
              transform: `translateX(${mockScrollBarTranslateDistance}px)`,
            }}
          ></div>
        </div>
      ) : null}
    </>
  );
};

export default CategoryNoResult;
